import { Badge } from '@/components/ui/Badge/badge';
import { deparmentCVA } from '@/components/ui/Badge/deparmentCVA';
import { cn } from '@/lib/utils';
import { LabelBadge } from '@/components/ui/LabelBadge';
import { UserBadge } from '@/components/ui/UserBadge/UserBadge';
import { labelQueries } from '@/features/labels/labels.queries';
import { projectQueries } from '@/features/projects/projects.queries';
import { userQueries } from '@/features/users/users.queries';
import { workQueries } from '@/features/works/works.queries';
import { queryClient } from '@/lib/query-client';
import { Label, Project, User, Work } from '@certhon/models';
import { EntityConfig } from './types';

const userConfig: EntityConfig<User> = {
  search: async () => {
    const users = await queryClient.fetchQuery(userQueries.list());
    // Filter out inactive users from selection dropdowns
    return users.filter((user) => user.active);
  },
  // FIXME: gracefull degradation
  getById: async (recnum: string) => {
    const users = await queryClient.fetchQuery(userQueries.list());
    const user = users.find((u) => u.recnum === parseInt(recnum));
    if (!user) throw new Error(`User ${recnum} not found`);
    return user;
  },
  getOptionKeywords: (user) => [user.loginname, user.name].filter(Boolean),
  renderBadge: (user, onUnselect) => (
    <UserBadge user={user} onUnselect={onUnselect} />
  ),
  getOptionLabel: (user) =>
    String(user.name || user.loginname || `Entity ${user.recnum}`),
  getOptionValue: (user) => user.recnum,
};

const labelConfig: EntityConfig<Label> = {
  search: async () => {
    return await queryClient.fetchQuery(labelQueries.list()); // no server-side search
  },
  getById: async (recnum: string) => {
    const labels = await queryClient.fetchQuery(labelQueries.list());
    const label = labels.find((l) => l.recnum === parseInt(recnum));
    // FIXME: gracefull degradation
    if (!label) throw new Error(`Label ${recnum} not found`);
    return label;
  },
  getOptionKeywords: (label) => [label.name, label.description].filter(Boolean),
  renderBadge: (label, onUnselect) => (
    <LabelBadge
      label={label}
      variant="outline"
      showUnselect={!!onUnselect}
      onUnselect={onUnselect}
    />
  ),
  getOptionValue: (label) => label.recnum,
  getOptionLabel: (label) => label.name,
};

const workConfig: EntityConfig<Work> = {
  search: async (_search, context) => {
    return queryClient.fetchQuery(workQueries.list(context?.projectId));
  },
  getById: async (recnum: string) => {
    return await queryClient.fetchQuery(workQueries.detail(parseInt(recnum)));
  },
  getOptionKeywords: (work) => [work.id, work.oms, work.afd].filter(Boolean),
  renderBadge: (work /* onUnselect */) => (
    <Badge
      variant="outline"
      className={cn(deparmentCVA({ variant: work.afd as '_' }), 'max-w-full')}
      title={work.oms ?? undefined}
    >
      <span className="truncate">{work.oms}</span>
    </Badge>
  ),
  getOptionValue: (work) => work.recnum,
  getOptionLabel: (work) => work.oms || work.id,
};

// Customer type derived from Project data (no dedicated customer entity)
export interface Customer {
  klant_id: number;
  klant_naam: string;
}

const customerConfig: EntityConfig<Customer> = {
  search: async () => {
    const projects = await queryClient.fetchQuery(
      projectQueries.list({ archived: false }),
    );
    // Extract unique customers from projects
    const customersMap = new Map<number, Customer>();
    projects.forEach((project) => {
      if (!customersMap.has(project.klant_id)) {
        customersMap.set(project.klant_id, {
          klant_id: project.klant_id,
          klant_naam: project.klant_naam,
        });
      }
    });
    return Array.from(customersMap.values()).sort((a, b) =>
      a.klant_naam.localeCompare(b.klant_naam),
    );
  },
  getById: async (recnum: string) => {
    const projects = await queryClient.fetchQuery(
      projectQueries.list({ archived: false }),
    );
    const project = projects.find((p) => p.klant_id === parseInt(recnum));
    if (!project) throw new Error(`Customer ${recnum} not found`);
    return {
      klant_id: project.klant_id,
      klant_naam: project.klant_naam,
    };
  },
  getOptionKeywords: (customer) => [customer.klant_naam],
  renderBadge: (customer) => (
    <Badge variant="outline">{customer.klant_naam}</Badge>
  ),
  getOptionValue: (customer) => customer.klant_id,
  getOptionLabel: (customer) => customer.klant_naam,
};

const projectConfig: EntityConfig<Project> = {
  search: async (_search, context) => {
    const allProjects = await queryClient.fetchQuery(
      projectQueries.list({ archived: false }),
    );
    // Filter by customer if context provided
    if (context?.customerId) {
      return allProjects.filter((p) => p.klant_id === context.customerId);
    }
    return allProjects;
  },
  getById: async (recnum: string) => {
    return await queryClient.fetchQuery(
      projectQueries.detail(parseInt(recnum)),
    );
  },
  getOptionKeywords: (project) =>
    [project.projectname, project.id, project.klant_naam].filter(Boolean),
  renderBadge: (project) => (
    <Badge variant="outline">{project.projectname}</Badge>
  ),
  getOptionValue: (project) => project.recnum,
  getOptionLabel: (project) => project.projectname || project.id,
};

export const entityConfigs = {
  user: userConfig,
  label: labelConfig,
  work: workConfig,
  customer: customerConfig,
  project: projectConfig,
} as const;

export type EntityType = keyof typeof entityConfigs;

// Type helpers for entity resolution
export type EntityTypeMap = {
  [K in EntityType]: Parameters<
    (typeof entityConfigs)[K]['getOptionKeywords'] // random
  >[0];
};

export type EntityIdMap = {
  [K in EntityType]: number;
};
